import React, { useState, useEffect, useRef } from "react";
import { NextIcon, UsersIcon } from "../icons/icons";
import { useTranslation } from "react-i18next";
import fa from "./locales/HomeHeroSection-fa.json";
import en from "./locales/HomeHeroSection-en.json";
import ar from "./locales/HomeHeroSection-ar.json";
import tr from "./locales/HomeHeroSection-tr.json";
import { FaDumbbell, FaChild, FaQuoteRight } from "react-icons/fa";
import { MdElderly, MdWoman, MdBusiness } from "react-icons/md";
import { GiMuscleUp } from "react-icons/gi";
import { motion, useMotionValue, useSpring, AnimatePresence } from "framer-motion";

// ─── Types ───────────────────────────────────────────────────────────────────
interface HeroSectionProps {
    onCompareClick: () => void;
}

// ─── Floating Particle ───────────────────────────────────────────────────────
const FloatingParticle: React.FC<{ x: number; delay: number; color?: string }> = ({ x, delay, color = "#155DFC" }) => (
    <motion.div
        className="absolute w-1 h-1 rounded-full pointer-events-none"
        style={{ left: `${x}%`, bottom: "10%", backgroundColor: color, opacity: 0.6 }}
        animate={{ y: [-0, -50, -0], opacity: [0, 0.7, 0], scale: [0, 1.2, 0] }}
        transition={{ duration: 2, delay, repeat: Infinity, ease: "easeInOut" }}
    />
);

// ─── Pulse Ring ──────────────────────────────────────────────────────────────
const PulseRing: React.FC<{ color?: string; size?: number }> = ({ color = "rgba(21,93,252,0.35)", size = 1.7 }) => (
    <>
        {[0, 0.45, 0.9].map((d) => (
            <motion.div
                key={d}
                className="absolute inset-0 rounded-2xl"
                style={{ border: `1.5px solid ${color}` }}
                initial={{ opacity: 0.5, scale: 1 }}
                animate={{ opacity: 0, scale: size }}
                transition={{ duration: 1.8, delay: d, repeat: Infinity, ease: "easeOut" }}
            />
        ))}
    </>
);

// ─── Glass Icon Card ─────────────────────────────────────────────────────────
interface GlassIconCardProps {
    icon: React.ReactNode;
    label: string;
    jtbd?: string;
    delay?: number;
    floatDelay?: number;
    position: { top?: string; bottom?: string; left?: string; right?: string };
    rotate?: number;
}

const GlassIconCard: React.FC<GlassIconCardProps> = ({ icon, label, jtbd, delay = 0, floatDelay = 0, position, rotate = 0 }) => {
    const [hovered, setHovered] = useState(false);

    return (
        <motion.div
            className="absolute flex flex-col items-center gap-1.5 cursor-pointer group"
            style={position}
            initial={{ opacity: 0, scale: 0.7, rotate: rotate - 5 }}
            animate={{ opacity: 1, scale: 1, rotate: rotate }}
            transition={{ duration: 0.6, delay, type: "spring", stiffness: 180 }}
            whileHover={{ scale: 1.12, rotate: rotate + (rotate >= 0 ? 4 : -4), zIndex: 30 }}
            whileTap={{ scale: 0.94 }}
            onHoverStart={() => setHovered(true)}
            onHoverEnd={() => setHovered(false)}
        >
            {/* Top Quote Tooltip Component */}
            <AnimatePresence>
                {hovered && (
                    <motion.div
                        className="absolute bottom-[100%] mb-3 left-1/2 -translate-x-1/2 z-50 flex flex-col items-center p-3 px-3.5 rounded-2xl pointer-events-none w-[190px] sm:w-[215px] text-center"
                        style={{
                            background: "linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(240, 246, 255, 0.96) 100%)",
                            border: "1px solid rgba(21, 93, 252, 0.25)",
                            backdropFilter: "blur(16px)",
                            WebkitBackdropFilter: "blur(16px)",
                            boxShadow: "0 14px 36px -6px rgba(21, 93, 252, 0.25), 0 4px 12px rgba(0, 0, 0, 0.05)",
                        }}
                        initial={{ opacity: 0, scale: 0.85, y: 8 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.85, y: 8 }}
                        transition={{ duration: 0.2, ease: "easeOut" }}
                    >
                        {/* Quote Header */}
                        <div className="flex items-center justify-between w-full mb-1">
                            <span className="text-[10px] font-bold tracking-tight px-2 py-0.5 rounded-md bg-[#155DFC]/10 text-[#155DFC]">
                                {label}
                            </span>
                            <FaQuoteRight className="text-[#155DFC]/35 text-xs" />
                        </div>

                        {/* Quote Statement */}
                        {jtbd && (
                            <p className="text-[11px] leading-relaxed text-[#293048] font-medium text-right w-full mt-0.5" style={{ fontFamily: "Vazirmatn, sans-serif" }}>
                                «{jtbd}»
                            </p>
                        )}

                        {/* Arrow Pointer */}
                        <div
                            className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 w-3 h-3 rotate-45"
                            style={{
                                background: "#EFF6FF",
                                borderRight: "1px solid rgba(21, 93, 252, 0.25)",
                                borderBottom: "1px solid rgba(21, 93, 252, 0.25)",
                            }}
                        />
                    </motion.div>
                )}
            </AnimatePresence>

            <motion.div
                className="relative w-[72px] h-[72px] rounded-2xl flex items-center justify-center"
                style={{
                    background: hovered
                        ? "linear-gradient(135deg, rgba(255,255,255,0.6) 0%, rgba(239,246,255,0.5) 100%)"
                        : "linear-gradient(135deg, rgba(255,255,255,0.28) 0%, rgba(255,255,255,0.08) 100%)",
                    border: `1px solid ${hovered ? "rgba(21,93,252,0.35)" : "rgba(255,255,255,0.3)"}`,
                    backdropFilter: "blur(16px)",
                    WebkitBackdropFilter: "blur(16px)",
                    boxShadow: hovered
                        ? "0 16px 40px -8px rgba(21,93,252,0.25), 0 4px 12px rgba(0,0,0,0.06), inset 0 1px 0 rgba(255,255,255,0.6)"
                        : "0 4px 16px rgba(0,0,0,0.07), inset 0 1px 0 rgba(255,255,255,0.5)",
                }}
                animate={{ y: [0, -7, 0] }}
                transition={{ y: { duration: 3 + floatDelay * 0.5, repeat: Infinity, ease: "easeInOut", delay: floatDelay } }}
            >
                {/* Top shimmer */}
                <motion.div
                    className="absolute top-0 left-2 right-2 h-[1px] rounded-full"
                    style={{ background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.8), transparent)" }}
                    animate={{ opacity: hovered ? 1 : 0.4 }}
                />

                {/* Pulse rings on hover */}
                {hovered && <PulseRing />}

                <motion.div animate={{ rotate: hovered ? [0, -10, 10, 0] : 0, scale: hovered ? 1.15 : 1 }} transition={{ duration: 0.4 }}>
                    {icon}
                </motion.div>
            </motion.div>
        </motion.div>
    );
};

// ─── Feature Chip ─────────────────────────────────────────────────────────────
interface FeatureChipProps {
    icon: React.ReactNode;
    title: string;
    description: string;
    index: number;
}

const FeatureChip: React.FC<FeatureChipProps> = ({ icon, title, description, index }) => {
    const [hovered, setHovered] = useState(false);
    const [particles, setParticles] = useState<{ id: number; x: number }[]>([]);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const spawnParticles = () => {
        const ps = Array.from({ length: 3 }, (_, i) => ({ id: Date.now() + i, x: 20 + Math.random() * 60 }));
        setParticles(ps);
        timerRef.current = setTimeout(() => setParticles([]), 2100);
    };
    useEffect(
        () => () => {
            if (timerRef.current) clearTimeout(timerRef.current);
        },
        []
    );

    return (
        <motion.div
            className="relative flex gap-3 cursor-pointer overflow-hidden rounded-2xl p-3"
            style={{
                background: hovered ? "linear-gradient(135deg, rgba(255,255,255,0.85) 0%, rgba(239,246,255,0.75) 100%)" : "transparent",
                border: `1px solid ${hovered ? "rgba(21,93,252,0.2)" : "transparent"}`,
                backdropFilter: hovered ? "blur(12px)" : "none",
                WebkitBackdropFilter: hovered ? "blur(12px)" : "none",
                boxShadow: hovered ? "0 8px 24px -6px rgba(21,93,252,0.14)" : "none",
                transition: "all 0.3s ease",
            }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 1.4 + index * 0.1 }}
            whileHover={{ scale: 1.04, y: -4 }}
            whileTap={{ scale: 0.97 }}
            onHoverStart={() => {
                setHovered(true);
                spawnParticles();
            }}
            onHoverEnd={() => setHovered(false)}
        >
            {/* Particles */}
            <div className="absolute inset-0 pointer-events-none overflow-hidden">
                {particles.map((p) => (
                    <FloatingParticle key={p.id} x={p.x} delay={0} />
                ))}
            </div>

            {/* Top shimmer */}
            <motion.div
                className="absolute top-0 left-4 right-4 h-[1px] rounded-full"
                style={{ background: "linear-gradient(90deg, transparent, #155DFC, transparent)" }}
                animate={{ opacity: hovered ? 0.6 : 0, scaleX: hovered ? 1 : 0 }}
                transition={{ duration: 0.3 }}
            />

            {/* Icon */}
            <motion.div className="flex-shrink-0 relative" animate={{ rotate: hovered ? [0, -8, 8, 0] : 0, scale: hovered ? 1.1 : 1 }} transition={{ duration: 0.4 }}>
                {hovered && (
                    <motion.div
                        className="absolute inset-0 rounded-xl"
                        style={{ border: "1.5px solid rgba(21,93,252,0.35)" }}
                        initial={{ scale: 1, opacity: 0.5 }}
                        animate={{ scale: 1.5, opacity: 0 }}
                        transition={{ duration: 0.9, repeat: Infinity }}
                    />
                )}
                {icon}
            </motion.div>

            {/* Text */}
            <div className="flex flex-col mt-1">
                <motion.h2 className="font-semibold mb-0.5 text-sm transition-colors duration-300" animate={{ color: hovered ? "#155DFC" : "#111827" }}>
                    {title}
                </motion.h2>
                <motion.p className="text-xs leading-relaxed transition-colors duration-300" animate={{ color: hovered ? "#6b7280" : "#5D697F" }}>
                    {description}
                </motion.p>
            </div>
        </motion.div>
    );
};

// ─── Main Hero Section ────────────────────────────────────────────────────────
const HeroSection: React.FC<HeroSectionProps> = ({ onCompareClick }) => {
    const { t, i18n } = useTranslation();
    const [badgeHovered, setBadgeHovered] = useState(false);
    const [btnHovered, setBtnHovered] = useState(false);

    // Magnetic button
    const btnX = useMotionValue(0);
    const btnY = useMotionValue(0);
    const springBtnX = useSpring(btnX, { stiffness: 200, damping: 18 });
    const springBtnY = useSpring(btnY, { stiffness: 200, damping: 18 });

    const btnRef = useRef<HTMLButtonElement>(null);
    const handleBtnMouseMove = (e: React.MouseEvent<HTMLButtonElement>) => {
        const rect = btnRef.current?.getBoundingClientRect();
        if (!rect) return;
        btnX.set((e.clientX - rect.left - rect.width / 2) * 0.25);
        btnY.set((e.clientY - rect.top - rect.height / 2) * 0.25);
    };
    const handleBtnMouseLeave = () => {
        btnX.set(0);
        btnY.set(0);
    };

    useEffect(() => {
        i18n.addResourceBundle("fa", "home", fa, true, true);
        i18n.addResourceBundle("en", "home", en, true, true);
        i18n.addResourceBundle("ar", "home", ar, true, true);
        i18n.addResourceBundle("tr", "home", tr, true, true);
    }, []);

    const handleSmoothScroll = () => {
        const target =
            document.getElementById("rings-section") ||
            document.getElementById("products-section") ||
            document.querySelector(".products-section");
        if (target) {
            target.scrollIntoView({ behavior: "smooth", block: "start" });
        } else {
            window.scrollTo({ top: window.innerHeight, behavior: "smooth" });
        }
        if (onCompareClick) onCompareClick();
    };

    const features = [
        {
            title: t("home:multiUserMonitoring"),
            description: t("home:familySafety"),
            icon: <UsersIcon />,
        },
        {
            title: t("home:aiPowered"),
            description: t("home:aiReports"),
            icon: (
                <div className="w-[54px] h-[56px] rounded-xl bg-gradient-to-b from-[#EAEDF2] to-white/0 flex items-center justify-center">
                    <img src="/icon/ai.svg" alt={t("home:aiPowered")} className="w-7 h-7" />
                </div>
            ),
        },
        {
            title: t("home:organizationalSolutions"),
            description: t("home:customSolutions"),
            icon: (
                <div className="w-[54px] h-[56px] rounded-xl bg-gradient-to-b from-[#EAEDF2] to-white/0 flex items-center justify-center">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" role="img" aria-label={t("home:organizationalSolutions")} color="#293048">
                        <title>{t("home:organizationalSolutions")}</title>
                        <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M7.93195 1.25H8.06805C9.37381 1.24997 10.4505 1.24994 11.3027 1.36606C12.1974 1.48796 12.9876 1.75375 13.6203 2.39495C14.2529 3.03615 14.5152 3.83695 14.6355 4.74369C14.7501 5.60736 14.75 6.6986 14.75 8.02194L14.75 22.75H7.93197C6.6262 22.75 5.54946 22.7501 4.69726 22.6339C3.80257 22.512 3.01241 22.2463 2.37973 21.6051C1.74706 20.9639 1.4848 20.1631 1.36451 19.2563C1.24994 18.3926 1.24997 17.3014 1.25 15.9781V8.02194C1.24997 6.6986 1.24994 5.60736 1.36451 4.74369C1.4848 3.83695 1.74706 3.03615 2.37973 2.39495C3.01241 1.75375 3.80257 1.48796 4.69726 1.36606C5.54946 1.24994 6.62619 1.24997 7.93195 1.25ZM4.95424 3.30317C4.2734 3.39594 3.95806 3.55951 3.74344 3.77702C3.52882 3.99453 3.36742 4.31412 3.27589 5.00413C3.18062 5.72225 3.17857 6.68121 3.17857 8.09091V15.9091C3.17857 17.3188 3.18062 18.2778 3.27589 18.9959C3.36742 19.6859 3.52882 20.0055 3.74344 20.223C3.95806 20.4405 4.2734 20.6041 4.95424 20.6968C5.66282 20.7934 6.60903 20.7955 8 20.7955H12.8214V8.09091C12.8214 6.68121 12.8194 5.72225 12.7241 5.00413C12.6326 4.31412 12.4712 3.99453 12.2566 3.77702C12.0419 3.55951 11.7266 3.39594 11.0458 3.30317C10.3372 3.20662 9.39097 3.20455 8 3.20455C6.60903 3.20455 5.66282 3.20662 4.95424 3.30317Z"
                            fill="#293048"
                        />
                        <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M4.5 7C4.5 6.44772 4.94772 6 5.5 6H6.5C7.05228 6 7.5 6.44772 7.5 7C7.5 7.55228 7.05228 8 6.5 8H5.5C4.94772 8 4.5 7.55228 4.5 7ZM8.5 7C8.5 6.44772 8.94772 6 9.5 6H10.5C11.0523 6 11.5 6.44772 11.5 7C11.5 7.55228 11.0523 8 10.5 8H9.5C8.94772 8 8.5 7.55228 8.5 7ZM4.5 11C4.5 10.4477 4.94772 10 5.5 10H6.5C7.05228 10 7.5 10.4477 7.5 11C7.5 11.5523 7.05228 12 6.5 12H5.5C4.94772 12 4.5 11.5523 4.5 11ZM8.5 11C8.5 10.4477 8.94772 10 9.5 10H10.5C11.0523 10 11.5 10.4477 11.5 11C11.5 11.5523 11.0523 12 10.5 12H9.5C8.94772 12 8.5 11.5523 8.5 11ZM4.5 15C4.5 14.4477 4.94772 14 5.5 14H6.5C7.05228 14 7.5 14.4477 7.5 15C7.5 15.5523 7.05228 16 6.5 16H5.5C4.94772 16 4.5 15.5523 4.5 15ZM8.5 15C8.5 14.4477 8.94772 14 9.5 14H10.5C11.0523 14 11.5 14.4477 11.5 15C11.5 15.5523 11.0523 16 10.5 16H9.5C8.94772 16 8.5 15.5523 8.5 15Z"
                            fill="#293048"
                        />
                        <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M21.9445 8.05546C21.4891 7.59999 20.9223 7.41432 20.2945 7.32992C19.6997 7.24995 18.9505 7.24997 18.0521 7.25H18.052H18.052L13.25 7.25V22.75L18.052 22.75H18.052C18.9505 22.75 19.6997 22.7501 20.2945 22.6701C20.9223 22.5857 21.4891 22.4 21.9445 21.9445C22.4 21.4891 22.5857 20.9223 22.6701 20.2945C22.7501 19.6997 22.75 18.9505 22.75 18.052V18.052V11.948V11.948C22.75 11.0495 22.7501 10.3003 22.6701 9.70552C22.5857 9.07773 22.4 8.51093 21.9445 8.05546ZM17.5 10.25C17.0858 10.25 16.75 10.5858 16.75 11C16.75 11.4142 17.0858 11.75 17.5 11.75H18.5C18.9142 11.75 19.25 11.4142 19.25 11C19.25 10.5858 18.9142 10.25 18.5 10.25H17.5ZM17.5 14.25C17.0858 14.25 16.75 14.5858 16.75 15C16.75 15.4142 17.0858 15.75 17.5 15.75H18.5C18.9142 15.75 19.25 15.4142 19.25 15C19.25 14.5858 18.9142 14.25 18.5 14.25H17.5Z"
                            fill="#293048"
                        />
                    </svg>
                </div>
            ),
        },
    ];

    const leftIcons: GlassIconCardProps[] = [
        {
            icon: <GiMuscleUp className="text-[32px] text-[#155DFC] drop-shadow-sm" />,
            label: t("home:icons.sport"),
            jtbd: t("home:iconJtbd.sport"),
            delay: 0.5,
            floatDelay: 0,
            position: { top: "8px", left: "8px" },
            rotate: -3,
        },
        {
            icon: <MdWoman className="text-[32px] text-[#155DFC] drop-shadow-sm" />,
            label: t("home:icons.womensHealth"),
            jtbd: t("home:iconJtbd.womensHealth"),
            delay: 0.65,
            floatDelay: 0.6,
            position: { top: "33%", right: "8px" },
            rotate: 2,
        },
        {
            icon: <MdElderly className="text-[32px] text-[#155DFC] drop-shadow-sm" />,
            label: t("home:icons.elderly"),
            jtbd: t("home:iconJtbd.elderly"),
            delay: 0.8,
            floatDelay: 1.2,
            position: { bottom: "16px", left: "8px" },
            rotate: -2,
        },
    ];

    const rightIcons: GlassIconCardProps[] = [
        {
            icon: <FaChild className="text-[32px] text-[#155DFC] drop-shadow-sm" />,
            label: t("home:icons.children"),
            jtbd: t("home:iconJtbd.children"),
            delay: 0.55,
            floatDelay: 0.3,
            position: { top: "8px", right: "8px" },
            rotate: 3,
        },
        {
            icon: <FaDumbbell className="text-[30px] text-[#155DFC] drop-shadow-sm" />,
            label: t("home:icons.gym"),
            jtbd: t("home:iconJtbd.gym"),
            delay: 0.7,
            floatDelay: 0.9,
            position: { top: "33%", left: "8px" },
            rotate: -2,
        },
        {
            icon: <MdBusiness className="text-[32px] text-[#155DFC] drop-shadow-sm" />,
            label: t("home:icons.humanResources"),
            jtbd: t("home:iconJtbd.humanResources"),
            delay: 0.85,
            floatDelay: 1.5,
            position: { bottom: "16px", right: "8px" },
            rotate: 3,
        },
    ];

    return (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: "easeOut" }} className="relative pt-0 sm:pt-14">
            {/* ── Ambient background light aura & dynamic mesh animation ── */}
            <motion.div
                className="absolute top-[-40px] left-1/2 -translate-x-1/2 w-[700px] h-[450px] rounded-full pointer-events-none z-0"
                style={{
                    background: "radial-gradient(ellipse at center, rgba(21,93,252,0.1) 0%, rgba(59,130,246,0.05) 45%, transparent 75%)",
                    filter: "blur(60px)",
                }}
                animate={{
                    scale: [1, 1.15, 0.95, 1],
                    opacity: [0.7, 1, 0.8, 0.7],
                    rotate: [0, 10, -10, 0]
                }}
                transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
            />
            <motion.div
                className="absolute top-10 left-1/4 w-[450px] h-[450px] rounded-full pointer-events-none z-0"
                style={{
                    background: "radial-gradient(circle, rgba(99,102,241,0.08) 0%, transparent 70%)",
                    filter: "blur(50px)",
                }}
                animate={{ scale: [1, 1.25, 1], x: [-20, 20, -20], y: [-10, 15, -10] }}
                transition={{ duration: 9, repeat: Infinity, ease: "easeInOut" }}
            />
            <motion.div
                className="absolute bottom-10 right-1/4 w-[420px] h-[420px] rounded-full pointer-events-none z-0"
                style={{
                    background: "radial-gradient(circle, rgba(14,165,233,0.09) 0%, transparent 70%)",
                    filter: "blur(50px)",
                }}
                animate={{ scale: [1, 1.2, 1], x: [15, -15, 15], y: [10, -15, 10] }}
                transition={{ duration: 11, repeat: Infinity, ease: "easeInOut", delay: 1 }}
            />

            <div className="container mx-auto px-4 sm:px-8 pt-0 md:pt-19">
                {/* ── Three-column header ── */}
                <div className="flex justify-between items-center pt-0 md:pt-6 flex-col md:flex-row gap-6 md:gap-8">
                    {/* Left icons column */}
                    <motion.div
                        className="hidden h-[400px] md:w-1/12 lg:w-2/12 md:block relative"
                        initial={{ opacity: 0, x: -50 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                    >
                        {leftIcons.map((cfg, i) => (
                            <GlassIconCard key={i} {...cfg} />
                        ))}
                    </motion.div>

                    {/* Center content */}
                    <motion.div className="text-center h-full order-1 md:order-2 flex-1" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.3 }}>
                        {/* Supported-by badge */}
                        <motion.div className="flex justify-center mb-6 lg:mb-8">
                            <motion.div
                                className="relative inline-flex items-center gap-2 rounded-full px-3 md:px-4 py-1.5 md:py-2 cursor-pointer overflow-hidden"
                                style={{
                                    background: badgeHovered ? "linear-gradient(90deg, #DBEAFE 0%, #EFF6FF 100%)" : "linear-gradient(90deg, rgba(219,234,254,0.8) 0%, rgba(239,246,255,0.8) 100%)",
                                    border: "1px solid rgba(255,255,255,0.5)",
                                    backdropFilter: "blur(8px)",
                                    WebkitBackdropFilter: "blur(8px)",
                                    boxShadow: badgeHovered
                                        ? "0 10px 28px -6px rgba(21,93,252,0.25), inset 0 1px 0 rgba(255,255,255,0.6)"
                                        : "0 4px 16px rgba(21,93,252,0.1), inset 0 1px 0 rgba(255,255,255,0.6)",
                                }}
                                whileHover={{ scale: 1.04, y: -2 }}
                                whileTap={{ scale: 0.97 }}
                                onHoverStart={() => setBadgeHovered(true)}
                                onHoverEnd={() => setBadgeHovered(false)}
                            >
                                {/* Shimmer sweep */}
                                <motion.div
                                    className="absolute inset-0 pointer-events-none"
                                    style={{
                                        background: "linear-gradient(105deg, transparent 30%, rgba(255,255,255,0.45) 50%, transparent 70%)",
                                        backgroundSize: "200% 100%",
                                    }}
                                    animate={{ backgroundPosition: badgeHovered ? ["200% 0", "-200% 0"] : "200% 0" }}
                                    transition={{ duration: 0.8, ease: "easeInOut" }}
                                />
                                <span className="text-[#293048] text-sm md:text-base mb-0.5 font-medium -tracking-[0.02em] relative z-10" style={{ fontFamily: "Vazirmatn, sans-serif" }}>
                                    {t("home:supportedBy")}
                                </span>
                                <motion.div
                                    className="flex items-center justify-center bg-white rounded-full px-3 py-2 shadow-sm relative z-10"
                                    whileHover={{ scale: 1.06 }}
                                    whileTap={{ scale: 0.95 }}
                                >
                                    <img src="../../../../media/HamrahAvval.svg" alt="mci" className="h-3 w-auto" />
                                </motion.div>
                            </motion.div>
                        </motion.div>

                        {/* Headline */}
                        <motion.h1 className="text-3xl md:text-4xl leading-relaxed font-medium" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 0.5 }}>
                            {t("home:firstService")}{" "}
                            <motion.span className="font-bold inline-block px-1.5" whileHover={{ scale: 1.05, color: "#155DFC" }} transition={{ duration: 0.2 }}>
                                {t("home:med30")}
                            </motion.span>{" "}
                            {t("home:with")}{" "}
                            <motion.span
                                className="font-bold bg-gradient-to-r from-[#0038A8] to-[#155DFC] bg-clip-text text-transparent inline-block px-2"
                                whileHover={{ scale: 1.04 }}
                                transition={{ duration: 0.2 }}
                            >
                                {t("home:wearableAI")}
                            </motion.span>{" "}
                            {t("home:inIran")}
                        </motion.h1>

                        {/* Description */}
                        <motion.p
                            className="mt-5 text-gray-600 leading-7 max-w-2xl mx-auto text-sm md:text-base"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 1, delay: 0.7 }}
                        >
                            {t("home:description")}
                        </motion.p>

                        {/* CTA Button — magnetic */}
                        <motion.button
                            ref={btnRef}
                            onClick={handleSmoothScroll}
                            onMouseMove={handleBtnMouseMove}
                            onMouseLeave={handleBtnMouseLeave}
                            onHoverStart={() => setBtnHovered(true)}
                            onHoverEnd={() => setBtnHovered(false)}
                            style={{ x: springBtnX, y: springBtnY }}
                            className="relative z-30 mt-8 text-sm sm:text-base font-semibold text-white px-7 py-3.5 rounded-2xl flex gap-3 items-center mx-auto cursor-pointer whitespace-nowrap overflow-hidden"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.9 }}
                            whileTap={{ scale: 0.96 }}
                        >
                            {/* Gradient base */}
                            <motion.div
                                className="absolute inset-0"
                                style={{ background: "linear-gradient(135deg, #155DFC 0%, #3B82F6 100%)" }}
                                animate={{ opacity: btnHovered ? 0 : 1 }}
                                transition={{ duration: 0.3 }}
                            />
                            {/* Hover gradient */}
                            <motion.div
                                className="absolute inset-0"
                                style={{ background: "linear-gradient(135deg, #3B82F6 0%, #155DFC 50%, #0042B3 100%)" }}
                                animate={{ opacity: btnHovered ? 1 : 0 }}
                                transition={{ duration: 0.3 }}
                            />
                            {/* Shimmer sweep */}
                            <motion.div
                                className="absolute inset-0 pointer-events-none"
                                style={{
                                    background: "linear-gradient(105deg, transparent 30%, rgba(255,255,255,0.25) 50%, transparent 70%)",
                                    backgroundSize: "250% 100%",
                                }}
                                animate={btnHovered ? { backgroundPosition: ["200% 0", "-200% 0"] } : {}}
                                transition={{ duration: 0.7, ease: "easeInOut" }}
                            />
                            {/* Border */}
                            <div
                                className="absolute inset-0 rounded-2xl"
                                style={{ border: "1px solid rgba(255,255,255,0.25)", boxShadow: btnHovered ? "0 16px 36px -6px rgba(21,93,252,0.45)" : "0 8px 20px -4px rgba(21,93,252,0.35)" }}
                            />

                            <span className="relative z-10">{t("home:viewProducts")}</span>
                            <motion.div
                                className="relative z-10"
                                animate={{ x: btnHovered ? [0, 6, 0] : [0, 4, 0] }}
                                transition={{ duration: btnHovered ? 0.6 : 1.5, repeat: Infinity, ease: "easeInOut" }}
                            >
                                <NextIcon />
                            </motion.div>
                        </motion.button>
                    </motion.div>

                    {/* Right icons column */}
                    <motion.div
                        className="hidden h-[400px] md:w-1/12 lg:w-2/12 md:block relative order-3"
                        initial={{ opacity: 0, x: 50 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8, delay: 0.4 }}
                    >
                        {rightIcons.map((cfg, i) => (
                            <GlassIconCard key={i} {...cfg} />
                        ))}
                    </motion.div>
                </div>

                {/* ── Feature chips row ── */}
                <motion.div
                    className="flex flex-col md:flex-row-reverse gap-2 md:gap-4 lg:gap-6 sm:items-center w-full justify-around py-8 relative z-30"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 1.2 }}
                >
                    {features.map((f, i) => (
                        <FeatureChip key={i} icon={f.icon} title={f.title} description={f.description} index={i} />
                    ))}

                    {/* Knowledge-based badge */}
                    <motion.div
                        className="rounded-2xl py-1 px-3 lg:px-4 mt-2 md:mt-0 w-full md:w-auto overflow-hidden relative cursor-pointer"
                        style={{
                            background: "rgba(234,237,242,0.5)",
                            backdropFilter: "blur(10px)",
                            WebkitBackdropFilter: "blur(10px)",
                            border: "1px solid rgba(255,255,255,0.7)",
                        }}
                        whileHover={{
                            scale: 1.03,
                            boxShadow: "0 8px 24px -6px rgba(21,93,252,0.12)",
                            borderColor: "rgba(21,93,252,0.2)",
                        }}
                        whileTap={{ scale: 0.97 }}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 1.7 }}
                    >
                        <div className="flex items-center justify-center">
                            <img src="../../../media/daneshbonyan.svg" alt="Delband Logo" className="h-14" />
                            <p className="text-[#5D697F] md:hidden xl:block text-sm ml-3">{t("home:knowledgeBased")}</p>
                        </div>
                    </motion.div>
                </motion.div>
            </div>
        </motion.div>
    );
};

export default HeroSection;
